import * as turf from '@turf/turf';

export type TipoDibujo =
  | 'Point'
  | 'LineString'
  | 'Polygon'
  | 'Circle'
  | 'MultiPolygon';

/**
 * Valor que viaja por el FormGroup (controles type / coordinates / radius).
 * El radio va en METROS, igual que `IGeoJSONCircle.radius` en el modelo y
 * en el backend (api-gestion compara distancias haversine en metros).
 */
export interface IValorGeojsonForm {
  type: string;
  coordinates?: unknown;
  radius?: number | null;
}

/** Cantidad de lados del polígono que aproxima un círculo */
export const PASOS_CIRCULO = 64;

/** Radio (en metros) con el que se crea un círculo al centrarlo en una dirección */
export const RADIO_CIRCULO_DEFECTO_M = 50;

/**
 * Crea la feature poligonal que representa un círculo dentro del store de
 * MapboxDraw. El centro y el radio (en metros) viven en `properties` como
 * fuente de verdad; el polígono es solo la representación visual.
 */
export function crearFeatureCirculo(
  center: [number, number],
  radiusM: number,
): GeoJSON.Feature {
  const feature = turf.circle(center, radiusM, {
    steps: PASOS_CIRCULO,
    units: 'meters',
  });
  feature.properties = { isCircle: true, center, radiusM };
  return feature;
}

/**
 * Convierte el valor del form en una Feature para MapboxDraw.
 * Devuelve null si no hay coordenadas cargadas.
 */
export function formToFeature(
  tipo: TipoDibujo,
  valor?: IValorGeojsonForm | null,
): GeoJSON.Feature | null {
  if (!valor?.coordinates) return null;

  if (tipo === 'Circle') {
    if (valor.radius == null) return null;
    return crearFeatureCirculo(
      valor.coordinates as [number, number],
      valor.radius,
    );
  }

  return {
    type: 'Feature',
    geometry: {
      type: tipo,
      coordinates: valor.coordinates,
    } as GeoJSON.Geometry,
    properties: {},
  };
}

/**
 * Convierte una Feature del store de Draw en el valor del form.
 * Para círculos lee la metadata de `properties`; si la feature fue movida
 * (draw.update), el centro se recalcula desde el polígono.
 */
export function featureToFormValue(
  tipo: TipoDibujo,
  feature: GeoJSON.Feature,
): IValorGeojsonForm {
  if (tipo === 'Circle' && feature.properties?.['isCircle']) {
    const radiusM = feature.properties['radiusM'] as number;
    const center = turf.centroid(feature as turf.AllGeoJSON).geometry
      .coordinates as [number, number];
    return { type: 'Point', coordinates: center, radius: radiusM };
  }

  const geom = feature.geometry as GeoJSON.Geometry & {
    coordinates: unknown;
  };
  return { type: geom.type, coordinates: geom.coordinates, radius: null };
}

/** Valor de form para "sin geometría" del tipo dado */
export function valorVacio(tipo: TipoDibujo): IValorGeojsonForm {
  return {
    type: tipo === 'Circle' ? 'Point' : tipo,
    coordinates: undefined,
    radius: null,
  };
}
